/**
 * Labstep
 *
 * @module grid/NewIndex/components/ColumnHeader/ContextMenu
 * @desc Entity DataGrid Context Menu
 */

import { EntityActionDelete } from 'labstep-web/components/Entity/Action/Delete';
import {
  useDataGridContext,
  useGridSmartTableProtocolConditionServiceFromContext,
} from 'labstep-web/contexts/grid/hook';
import { useParamsStateContext } from 'labstep-web/contexts/params-state/hook';
import { Dropdown } from 'labstep-web/core/Dropdown';
import { DropdownPortal } from 'labstep-web/core/Dropdown/Portal';
import { Icon } from 'labstep-web/core/Icon';
import { useToolPanelContext } from 'labstep-web/grid/Index/components/ToolPanel/context';
import { GridNewIndexService } from 'labstep-web/grid/NewIndex/services/grid-new-index.service';
import type { SmartTableColDef } from 'labstep-web/grid/SmartTableProtocolCondition/coldefs/types';
import { ToolPanelState } from 'labstep-web/grid/SmartTableProtocolCondition/components/ToolPanel';
import { GridSmartTableProtocolValueService } from 'labstep-web/grid/SmartTableProtocolValue/services/grid-smart-table-protocol-value.service';
import React from 'react';
import type { GridSmartTableColumnHeaderContextMenuProps } from './types';

export const GridNewIndexColumnHeaderContextMenu: React.FC<
  GridSmartTableColumnHeaderContextMenuProps
> = ({
  columnApi,
  column,
  firstItems,
  lastItems,
  trigger,
  experimentOrProtocol,
  protocolValue,
  metadata,
}) => {
  const ctx = useToolPanelContext();

  const { agGrid } = useDataGridContext();
  const gridSmartTableProtocolConditionService =
    useGridSmartTableProtocolConditionServiceFromContext();
  const { entityView } = useParamsStateContext();

  const dropdownItems: React.ReactNode[] = firstItems
    ? [firstItems]
    : [];

  const afterDividerItems = [];

  const colDef = column.getColDef() as SmartTableColDef;

  const parts: string[] = (colDef.colId || '').split(':');

  if (
    parts.length >= 4 &&
    parts[0] === 'metadata' &&
    (parts[1] === 'experiment' || parts[1] === 'protocol') &&
    ctx &&
    metadata
  ) {
    dropdownItems.push(
      <Dropdown.Item
        key="configure"
        onClick={(): void => {
          ctx.setActiveVariable(metadata);
          ctx.setToolPanelState(ToolPanelState.add_data_field);
          agGrid?.api?.setSideBarVisible(true);
        }}
      >
        <Icon name="cog" /> Configure
      </Dropdown.Item>,
    );
  }

  if (
    parts.length >= 3 &&
    parts[0] === 'protocol_value' &&
    ctx &&
    protocolValue
  ) {
    dropdownItems.push(
      <Dropdown.Item
        key="configure"
        onClick={(): void => {
          agGrid?.api?.setSideBarVisible(true);
          ctx.setToolPanelState(ToolPanelState.add_inventory_field);
          ctx.setActiveVariable(protocolValue);
        }}
      >
        <Icon name="cog" /> Configure
      </Dropdown.Item>,
    );
  }

  if (
    parts.length >= 4 &&
    parts[0] === 'metadata' &&
    (parts[1] === 'experiment' || parts[1] === 'protocol') &&
    metadata &&
    entityView &&
    gridSmartTableProtocolConditionService
  ) {
    dropdownItems.push(
      <EntityActionDelete
        key="delete-metadata"
        dataCy="grid/SmartTableProtocolCondition/components/ColumnHeader/ContextMenu:data-field-delete"
        entity={metadata}
        actionComponentProps={{ type: 'option' }}
        options={{
          onSuccess: () => {
            gridSmartTableProtocolConditionService.deleteDataFieldColDefs(
              metadata,
              entityView,
            );
          },
        }}
      />,
    );
  }

  if (
    parts.length === 3 &&
    parts[0] === 'protocol_value' &&
    parts[3] === 'resource_item_name' &&
    protocolValue &&
    entityView &&
    gridSmartTableProtocolConditionService
  ) {
    dropdownItems.push(
      <EntityActionDelete
        key="delete-protocol-value"
        dataCy="grid/SmartTableProtocolCondition/components/ColumnHeader/ContextMenu:inventory-field-delete"
        entity={protocolValue}
        actionComponentProps={{ type: 'option' }}
        options={{
          onSuccess: () => {
            gridSmartTableProtocolConditionService.deleteInventoryFieldColDefs(
              protocolValue,
              entityView,
            );
          },
        }}
      />,
    );
  }

  if (
    experimentOrProtocol &&
    !experimentOrProtocol.isLocked &&
    GridNewIndexService.isHideable(column)
  ) {
    afterDividerItems.push(
      <Dropdown.Item
        key="hide-column"
        onClick={(): void =>
          columnApi.setColumnVisible(column, false)
        }
      >
        <Icon name="eye slash" /> Hide from View
      </Dropdown.Item>,
    );
  }

  if (lastItems) {
    afterDividerItems.push(
      <React.Fragment key="last-items">{lastItems}</React.Fragment>,
    );
  }
  if (dropdownItems.length && afterDividerItems.length) {
    dropdownItems.push(<Dropdown.Divider key="main-divider" />);
  }
  dropdownItems.push(...afterDividerItems);
  if (colDef.columnOptions) {
    dropdownItems.push(<Dropdown.Divider key="options-divider" />);
    dropdownItems.push(
      <React.Fragment key="column-options">
        {colDef.columnOptions}
      </React.Fragment>,
    );
  }
  return dropdownItems.length ? (
    <DropdownPortal trigger={trigger}>{dropdownItems}</DropdownPortal>
  ) : (
    trigger
  );
};
